import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Building2 } from "lucide-react";
import { monthLabel, currentMonthLocal } from "./format";

export function OverheadsDialog({ open, onClose, canEdit, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ month: currentMonthLocal(), label: "", amount: "", currency: "EUR" });

  async function load() {
    setLoading(true);
    try { setRows((await api.get("/pnl/overheads")).data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { if (open) load(); }, [open]);

  async function add() {
    if (!form.month) return toast.error("Alege luna");
    if (!(parseFloat(form.amount) >= 0)) return toast.error("Completează suma");
    setBusy(true);
    try {
      await api.post("/pnl/overheads", { ...form, amount: parseFloat(form.amount) });
      toast.success("Regie adăugată");
      setForm({ ...form, label: "", amount: "" });
      await load(); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  async function remove(r) {
    if (!window.confirm(`Ștergi „${r.label}" din ${monthLabel(r.month)}?`)) return;
    try { await api.delete(`/pnl/overheads/${r.id}`); toast.success("Șters"); await load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  }

  const byMonth = rows.reduce((m, r) => { (m[r.month] ||= []).push(r); return m; }, {});
  const months = Object.keys(byMonth).sort().reverse();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="overheads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold"><Building2 className="h-5 w-5 text-[#3d68b1]" /> Regie lunară a firmei</DialogTitle>
          <DialogDescription>Chirie, utilități, administrativ… Suma fiecărei luni se repartizează automat pe proiecte, proporțional cu orele (pontate sau, dacă nu există, planificate).</DialogDescription>
        </DialogHeader>

        {canEdit && (
          <div className="surface-inset p-3 grid grid-cols-2 md:grid-cols-[170px_1fr_220px_auto] gap-3 items-end" data-testid="overhead-form">
            <label className="text-[11px] text-slate-500">Luna
              <input type="month" data-testid="overhead-month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="pio-input w-full min-w-[160px]" /></label>
            <label className="text-[11px] text-slate-500">Denumire
              <input data-testid="overhead-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="ex. Chirie sediu" className="pio-input w-full" /></label>
            <label className="text-[11px] text-slate-500">Sumă
              <div className="flex gap-1">
                <input type="number" step="any" min="0" data-testid="overhead-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="pio-input w-full min-w-[120px] font-mono" />
                <select data-testid="overhead-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="pio-input w-[80px] shrink-0"><option>EUR</option><option>RON</option></select>
              </div></label>
            <button data-testid="overhead-save" onClick={add} disabled={busy}
              className="h-9 flex items-center justify-center gap-1.5 px-3 rounded-lg text-[12px] font-medium bg-[#3d68b1] text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Adaugă
            </button>
          </div>
        )}

        <div className="max-h-[50vh] overflow-y-auto mt-3">
          {loading ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            : months.length === 0 ? <div className="py-10 text-center text-slate-400 text-sm" data-testid="overheads-empty">Nicio regie lunară introdusă încă.</div>
            : months.map((m) => (
              <div key={m} className="mb-3" data-testid={`overhead-month-${m}`}>
                <div className="flex items-center justify-between text-[12px] font-semibold text-slate-700 mb-1">
                  <span>{monthLabel(m)}</span>
                  <span className="tabular-nums">{byMonth[m].reduce((s, r) => s + (r.currency === "EUR" ? r.amount : 0), 0).toLocaleString("ro-RO")} € {byMonth[m].some((r) => r.currency === "RON") && "+ RON"}</span>
                </div>
                <div className="surface divide-y divide-slate-100">
                  {byMonth[m].map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2 text-[13px]" data-testid={`overhead-row-${r.id}`}>
                      <span className="text-slate-700">{r.label}</span>
                      <span className="flex items-center gap-3">
                        <span className="tabular-nums font-semibold">{(r.amount || 0).toLocaleString("ro-RO", { maximumFractionDigits: 2 })} {r.currency}</span>
                        {canEdit && <button data-testid={`overhead-delete-${r.id}`} onClick={() => remove(r)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
